import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="subscription-plan-selector"
export default class extends Controller {
  static targets = ["radio", "label", "seatsInput", "newCost", "difference", "submitButton", "photoLimit", "enrichmentLimit"]
  static values = {
    currentPlanId: Number,
    currentSeatPrice: Number,
    currentSeats: Number
  }

  connect() {
    this.updateSelection()
    this.updateCosts()
    // Initialize limits for the current plan
    const currentRadio = this.radioTargets.find(radio => radio.checked)
    if (currentRadio) {
      this.updateLimits(currentRadio)
    }
  }

  select(event) {
    // Clear all selections first
    this.labelTargets.forEach(label => {
      label.classList.remove('border-blue-500', 'bg-blue-50', 'text-blue-700')
      label.classList.add('border-gray-200')
    })

    // Find the corresponding label for the selected radio button
    const selectedRadio = event.target
    const correspondingLabel = this.labelTargets.find(label =>
      label.getAttribute('for') === selectedRadio.id
    )

    // Style the selected plan
    if (correspondingLabel) {
      correspondingLabel.classList.remove('border-gray-200')
      correspondingLabel.classList.add('border-blue-500', 'bg-blue-50', 'text-blue-700')
    }

    // Update seat limit based on selected plan
    const maxSeats = parseInt(selectedRadio.dataset.planMaxSeats)
    if (this.hasSeatsInputTarget && maxSeats < 999999 && this.seatsInputTarget.value > maxSeats) {
      this.seatsInputTarget.value = maxSeats
    }

    // Update usage limits display
    this.updateLimits(selectedRadio)

    this.updateCosts()
  }

  updateSelection() {
    // Find any pre-selected radio button and update styling
    const checkedRadio = this.radioTargets.find(radio => radio.checked)
    if (checkedRadio) {
      const correspondingLabel = this.labelTargets.find(label =>
        label.getAttribute('for') === checkedRadio.id
      )

      if (correspondingLabel) {
        // Clear all first
        this.labelTargets.forEach(label => {
          label.classList.remove('border-blue-500', 'bg-blue-50', 'text-blue-700')
          label.classList.add('border-gray-200')
        })

        // Style the checked one
        correspondingLabel.classList.remove('border-gray-200')
        correspondingLabel.classList.add('border-blue-500', 'bg-blue-50', 'text-blue-700')
      }
    }
  }

  increaseSeats() {
    const currentValue = parseInt(this.seatsInputTarget.value)
    const selectedRadio = this.radioTargets.find(radio => radio.checked)
    const maxSeats = selectedRadio ? parseInt(selectedRadio.dataset.planMaxSeats) : 999999

    if (currentValue < maxSeats) {
      this.seatsInputTarget.value = currentValue + 1
      this.updateCosts()
    }
  }

  decreaseSeats() {
    const currentValue = parseInt(this.seatsInputTarget.value)
    if (currentValue > 1) {
      this.seatsInputTarget.value = currentValue - 1
      this.updateCosts()
    }
  }

  updateCosts() {
    const selectedRadio = this.radioTargets.find(radio => radio.checked)
    if (!selectedRadio) return

    const planPrice = parseInt(selectedRadio.dataset.planPrice)
    const seats = this.hasSeatsInputTarget ? parseInt(this.seatsInputTarget.value) || 1 : this.currentSeatsValue

    if (isNaN(planPrice) || isNaN(seats)) return

    const newMonthlyCost = (planPrice * seats) / 100
    const currentMonthlyCost = (this.currentSeatPriceValue * this.currentSeatsValue) / 100
    const difference = newMonthlyCost - currentMonthlyCost

    // Update displays
    if (this.hasNewCostTarget) {
      this.newCostTarget.textContent = '$' + newMonthlyCost.toFixed(2)
    }

    if (this.hasDifferenceTarget) {
      const diffText = (difference >= 0 ? '+' : '') + '$' + difference.toFixed(2)
      this.differenceTarget.textContent = diffText
      this.differenceTarget.className = difference < 0 ? 'text-green-600 font-medium' : 'text-gray-500'
    }

    // Enable/disable submit button based on changes
    if (this.hasSubmitButtonTarget) {
      const hasChanges = parseInt(selectedRadio.value) !== this.currentPlanIdValue || seats !== this.currentSeatsValue
      this.submitButtonTarget.disabled = !hasChanges
    }
  }

  updateLimits(selectedRadio) {
    const photoLimit = parseInt(selectedRadio.dataset.photoLimit)
    const enrichmentLimit = parseInt(selectedRadio.dataset.enrichmentLimit)

    // Update photo limit display
    if (this.hasPhotoLimitTarget) {
      if (photoLimit === 0) {
        this.photoLimitTarget.textContent = 'Pay per use'
      } else {
        this.photoLimitTarget.textContent = photoLimit.toLocaleString() + ' free'
      }
    }

    // Update enrichment limit display
    if (this.hasEnrichmentLimitTarget) {
      if (enrichmentLimit === 0) {
        this.enrichmentLimitTarget.textContent = 'Pay per use'
      } else {
        this.enrichmentLimitTarget.textContent = enrichmentLimit.toLocaleString() + ' free'
      }
    }
  }
}